/**
 * Renders a shape primitive node (rectangle, ellipse, polygon, line).
 */

import { Box } from "@mui/material";
import React from "react";
import { resolveStyles } from "../layout-types";
import type { PrimitiveNode, InjectedData } from "../layout-types";

interface LayoutShapeProps {
  node: PrimitiveNode;
  data: InjectedData;
}

type PolygonShapeType =
  | "triangle"
  | "rightTriangle"
  | "trapezoid"
  | "parallelogram"
  | "pentagon"
  | "hexagon"
  | "octagon"
  | "star";

const toCssLength = (value: unknown): string | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value)}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
};

const POLYGON_POINTS: Record<PolygonShapeType, string> = {
  triangle: "50,0 100,100 0,100",
  rightTriangle: "0,0 0,100 100,100",
  trapezoid: "20,0 80,0 100,100 0,100",
  parallelogram: "20,0 100,0 80,100 0,100",
  pentagon: "50,0 100,38 82,100 18,100 0,38",
  hexagon: "25,0 75,0 100,50 75,100 25,100 0,50",
  octagon: "30,0 70,0 100,30 100,70 70,100 30,100 0,70 0,30",
  star: "50,0 62,36 100,36 69,58 81,100 50,74 19,100 31,58 0,36 38,36",
};

const parseBorder = (
  ...borderCandidates: Array<unknown>
): { width: number; color: string } => {
  const raw = borderCandidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim().length > 0,
  ) as string | undefined;
  if (!raw) {
    return { width: 0, color: "transparent" };
  }

  const parts = raw.trim().split(/\s+/);
  const widthToken = parts.find((part) => /px$/i.test(part));
  const parsedWidth = Number.parseFloat(widthToken ?? "");
  const width = Number.isFinite(parsedWidth) ? Math.max(0, parsedWidth) : 1;

  const colorCandidate =
    parts[parts.length - 1] &&
    !/(solid|dashed|dotted|double|none)/i.test(parts[parts.length - 1]!)
      ? parts[parts.length - 1]!
      : "#111111";

  return { width, color: colorCandidate };
};

const LayoutShape: React.FC<LayoutShapeProps> = ({ node, data }) => {
  const resolved = resolveStyles(node.style, data);

  const isHidden = resolved.visibility === false;
  if (isHidden) return null;

  const shapeType = node.shapeType ?? "rectangle";

  const baseStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    backgroundColor: (resolved.backgroundColor as string) ?? "#cccccc",
    opacity: resolved.opacity as number | undefined,
    border: resolved.border as string | undefined,
    borderTop: resolved.borderTop as string | undefined,
    borderRight: resolved.borderRight as string | undefined,
    borderBottom: resolved.borderBottom as string | undefined,
    borderLeft: resolved.borderLeft as string | undefined,
    borderTopLeftRadius: toCssLength(resolved.borderTopLeftRadius),
    borderTopRightRadius: toCssLength(resolved.borderTopRightRadius),
    borderBottomRightRadius: toCssLength(resolved.borderBottomRightRadius),
    borderBottomLeftRadius: toCssLength(resolved.borderBottomLeftRadius),
    boxSizing: "border-box",
  };

  if (shapeType === "ellipse" || shapeType === "oval") {
    baseStyle.borderRadius = "50%";
  } else if (shapeType === "square") {
    // Keep square as a distinct preset at creation time, but still fill the node box
    // so selection and resize handles stay visually aligned in the editor.
    baseStyle.borderRadius = toCssLength(resolved.borderRadius);
  } else if (shapeType === "rectangle") {
    baseStyle.borderRadius = toCssLength(resolved.borderRadius);
  } else if (shapeType === "line") {
    baseStyle.height = "2px";
    baseStyle.alignSelf = "center";
    baseStyle.borderRadius = undefined;
  }

  if (shapeType in POLYGON_POINTS) {
    const polygonType = shapeType as PolygonShapeType;
    const border = parseBorder(
      resolved.border,
      resolved.borderTop,
      resolved.borderRight,
      resolved.borderBottom,
      resolved.borderLeft,
    );

    return (
      <Box sx={{ width: "100%", height: "100%", opacity: baseStyle.opacity }}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon
            points={POLYGON_POINTS[polygonType]}
            fill={(resolved.backgroundColor as string) ?? "#cccccc"}
            stroke={border.color}
            strokeWidth={border.width}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </Box>
    );
  }

  return <Box sx={baseStyle} />;
};

export default LayoutShape;
